import { useState } from 'react';
import { useQuery, useMutation, skipToken } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import DialogContentText from '@mui/material/DialogContentText';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';

import {
    TrainingEditDocument,
    UpdateTrainingDocument,
    IContentTypeValue,
} from 'graphql/types';
import { PageDrawer, DrawerActionDropdown } from 'common/components/PageDrawer';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useApolloError } from 'common/hooks/useApolloError';
import { PageTitle } from 'common/components/PageTitle';
import { TrainingForm, ITrainingFormProps } from 'training/components/forms';
import { Loader } from 'common/components/Loader';
import { NotFound } from 'common/components/NotFound';
import { getUrl } from 'route/utils/getUrl';
import { AlertDialog } from 'common/components/AlertDialog';
import { Button } from 'common/components/Button';
import { TrainingCopyAction } from 'training/actions/TrainingCopyAction';
import { gotoTrainingPage } from 'training/utils/gotoRoutes';
import { getTrainingPermissions } from 'training/utils/permissions';
import { AuthorButton } from 'user/components/AuthorButton';

interface IProps {
    id?: string;
    open?: boolean;
    onClose?(): void;
}

export const TrainingUpdateDrawerContent = ({ id, open, onClose }: IProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    const navigate = useNavigate();
    const { showApolloError } = useApolloError();

    const [showArchiveAlert, setShowArchiveAlert] = useState<boolean>(false);

    const { data, loading } = useQuery(
        TrainingEditDocument,
        id ? { variables: { id }, nextFetchPolicy: 'cache-only' } : skipToken
    );

    const [updateTraining, { loading: loadingUpdate }] = useMutation(
        UpdateTrainingDocument
    );

    const training = data?.training;
    const { canUpdate } = getTrainingPermissions(training);

    if (loading) {
        return (
            <PageDrawer open={open} onClose={onClose}>
                <Loader />
            </PageDrawer>
        );
    }

    if (!training || !canUpdate) {
        return (
            <PageDrawer open={open} onClose={onClose}>
                <NotFound />
            </PageDrawer>
        );
    }

    const {
        image,
        title,
        titleColor,
        introduction,
        subtitle,
        overviewImage,
        estimatedTimeSpent,
        passThreshold,
        extraFields,
        startDate,
        endDate,
        isActive,
        showParticipants,
        enableChat,
        showSharedFiles,
        ref,
        syncWithAfas,
    } = training;

    const handleSubmit: ITrainingFormProps['onSubmit'] = async (values) => {
        if (!id) return;

        let response;

        try {
            response = await updateTraining({
                variables: { id, training: values },
                update: (cache) => {
                    cache.evict({ fieldName: 'paginatedTrainings' });
                },
            });
        } catch (error) {
            showApolloError(error);
        }

        const updatedTraining = response?.data?.updateTraining?.training;

        if (!updatedTraining) return;

        displaySnackbar(translate('trainingActionSuccess.update'), {
            variant: 'success',
        });

        onClose?.();
    };

    const handleArchive = async () => {
        if (!id) return;

        try {
            await updateTraining({
                variables: {
                    id,
                    training: { archived: true },
                },
                update: (cache) => {
                    cache.evict({
                        id: `Training:${training.id}`,
                    });
                    cache.gc();
                },
            });
        } catch (error) {
            // Close alert on error
            setShowArchiveAlert(false);
            showApolloError(error);

            return;
        }

        displaySnackbar(
            translate('trainingActionSuccess.archive', { count: 1 }),
            {
                variant: 'success',
            }
        );

        // Redirect to home after archiving training
        navigate(getUrl('HOME'));
    };

    const drawerActions = (
        <TrainingCopyAction
            id={training.id}
            onSuccess={(newTraining) =>
                // Redirect to new copy after copying training
                gotoTrainingPage(navigate, newTraining.id)
            }
        >
            {({ copyTraining }) => (
                <Stack direction="row" spacing={1}>
                    {training.id && (
                        <AuthorButton
                            contentType={IContentTypeValue.Training}
                            objectId={training.id}
                        />
                    )}

                    <DrawerActionDropdown>
                        <MenuItem onClick={() => setShowArchiveAlert(true)}>
                            {translate('toArchive')}
                        </MenuItem>
                        <MenuItem onClick={copyTraining}>
                            {translate('copy')}
                        </MenuItem>
                    </DrawerActionDropdown>
                </Stack>
            )}
        </TrainingCopyAction>
    );

    const initialValues = {
        image,
        title,
        titleColor,
        introduction,
        subtitle,
        overviewImage,
        estimatedTimeSpent,
        passThreshold,
        extraFields,
        startDate: startDate ? new Date(startDate) : null,
        endDate: endDate ? new Date(endDate) : null,
        isActive,
        showParticipants,
        enableChat,
        showSharedFiles,
        ref,
        syncWithAfas,
    };

    return (
        <PageDrawer actions={drawerActions} open={open} onClose={onClose}>
            <PageTitle>{translate('editTraining')}</PageTitle>

            <TrainingForm
                edit
                initialValues={initialValues}
                onClose={onClose}
                onSubmit={handleSubmit}
            />

            <AlertDialog
                actions={
                    <>
                        <Button
                            autoFocus
                            color="primary"
                            loading={loadingUpdate}
                            variant="contained"
                            onClick={handleArchive}
                        >
                            {translate('toArchive')}
                        </Button>
                        <Button
                            disabled={loadingUpdate}
                            variant="contained"
                            onClick={() => {
                                setShowArchiveAlert(false);
                            }}
                        >
                            {translate('cancel')}
                        </Button>
                    </>
                }
                open={showArchiveAlert}
                title={translate('archiveTrainingMessage.title')}
            >
                <DialogContentText color="text.primary" variant="body2">
                    {translate('archiveTrainingMessage.text')}
                </DialogContentText>
            </AlertDialog>
        </PageDrawer>
    );
};
